import { ReactElement, ReactNode, useMemo } from 'react'
import MultiEditOpenings from '@editor/pages/StructuralPlanning/components/MultiEdit/components/MultiEditOpenings'
import { CorneredSection } from '@ui2/structure'
import { useEditElementStore, useModelStore } from '@editorStores'
import IFCOpeningEditForm from '../IFCOpeningEditForm'

interface IFCOpeningMultiEditProps {
  /** Rendered when no wall opening is selected. */
  emptyState?: ReactNode
}

/**
 * Edits the currently selected wall opening(s) during IFC import: a single selected opening is
 * edited directly, two or more are aligned together via {@link MultiEditOpenings}. Shared by the
 * guided "Öffnungen ausrichten" step and the non-guided planar edit drawer.
 */
const IFCOpeningMultiEdit = ({ emptyState = null }: IFCOpeningMultiEditProps): ReactElement => {
  const walls = useModelStore(state => state.model.walls)
  const multiActiveOpenings = useEditElementStore(state => state.multiActiveOpenings)
  const setMultiActiveOpenings = useEditElementStore(state => state.setMultiActiveOpenings)

  // A single selected opening is edited directly; two or more are aligned together.
  const singleOpeningGuid = multiActiveOpenings.length === 1 ? multiActiveOpenings[0] : null
  const singleOpeningWallGuid = useMemo(() => {
    if (!singleOpeningGuid) return null
    const wall = walls.find(w =>
      (w.openings || []).some(opening => opening.guid === singleOpeningGuid),
    )
    return wall?.guid ?? null
  }, [singleOpeningGuid, walls])

  if (multiActiveOpenings.length > 1) {
    return (
      <CorneredSection>
        <MultiEditOpenings
          resetElements={() => setMultiActiveOpenings([])}
          selectedElements={multiActiveOpenings}
          onUseOpeningAsReference={openingGuid =>
            setMultiActiveOpenings([
              openingGuid,
              ...multiActiveOpenings.filter(guid => guid !== openingGuid),
            ])
          }
        />
      </CorneredSection>
    )
  }

  if (singleOpeningGuid) {
    return (
      <CorneredSection>
        <IFCOpeningEditForm
          wallGuid={singleOpeningWallGuid}
          openingGuid={singleOpeningGuid}
          onDeselect={() => setMultiActiveOpenings([])}
        />
      </CorneredSection>
    )
  }

  return <>{emptyState}</>
}

export default IFCOpeningMultiEdit
